import {
  LEGACY_APP_HOSTS,
  SITE_HOSTS,
  isLocalHost,
  siteOrigin,
  siteOriginForHost,
} from "@juristai/shared/src/routing/siteOrigin";

export const REDIRECT_QUERY_KEYS = ["redirect_url", "redirectUrl", "redirect"];
export const AUTH_CONTEXT_QUERY_KEYS = [
  "inviteToken",
  "inviteEmail",
  "email",
  "accountType",
  "account_type",
];
const ORGANIZATION_INVITE_APP_IDS = new Set(["1", "2", "3", "4", "5"]);

// The home site and every practice area are one site, served on juristai.org
// and nougat.law. A session lives on the origin it was created on, so every
// auth step stays on the visitor's own origin and no URL ever carries a token.

// Kept for callers that still pass users through /login after an SSO launch.
export const SSO_LANDING_PATHNAME = "/login";

const toSearchParams = (search) => {
  if (search instanceof URLSearchParams) {
    return search;
  }

  return new URLSearchParams(search || "");
};

export const getRedirectParam = (search) => {
  const searchParams = toSearchParams(search);

  for (const key of REDIRECT_QUERY_KEYS) {
    const value = searchParams.get(key);
    const normalizedValue = typeof value === "string" ? value.trim() : "";

    if (normalizedValue) {
      return { key, value: normalizedValue };
    }
  }

  return null;
};

export const getRedirectUrl = (search) => getRedirectParam(search)?.value || null;

export const getDashboardLoginRedirectUrl = (appId, location = {}) => {
  const normalizedAppId = String(appId || "").trim();
  const searchParams = toSearchParams(location.search);

  if (!getRedirectParam(searchParams) && /^[1-5]$/.test(normalizedAppId)) {
    searchParams.set("redirect_url", `/dashboard/${normalizedAppId}`);
  }

  const search = searchParams.toString();
  return `/login${search ? `?${search}` : ""}${location.hash || ""}`;
};

export const getAuthContextParams = (search) => {
  const searchParams = toSearchParams(search);

  return AUTH_CONTEXT_QUERY_KEYS.reduce((accumulator, key) => {
    const value = searchParams.get(key);
    const normalizedValue = typeof value === "string" ? value.trim() : "";

    if (normalizedValue) {
      accumulator[key] = normalizedValue;
    }

    return accumulator;
  }, {});
};

export const appendPreservedRedirectParam = (path, search) => {
  const redirectParam = getRedirectParam(search);
  const authContextParams = getAuthContextParams(search);

  if (!redirectParam && Object.keys(authContextParams).length === 0) {
    return path;
  }

  const [basePath, existingSearch = ""] = path.split("?");
  const nextSearchParams = new URLSearchParams(existingSearch);
  if (redirectParam) {
    nextSearchParams.set(redirectParam.key, redirectParam.value);
  }
  Object.entries(authContextParams).forEach(([key, value]) => {
    nextSearchParams.set(key, value);
  });

  return `${basePath}?${nextSearchParams.toString()}`;
};

const mapLegacyAppPath = (pathname, appId) => {
  const normalized = typeof pathname === "string" && pathname ? pathname : "/";
  const knownPrefixes = ["/dashboard", "/caseflow", "/connections", "/integrations", "/inbox-review"];
  const prefix = knownPrefixes.find(
    (candidate) => normalized === candidate || normalized.startsWith(`${candidate}/`),
  );

  if (!prefix) {
    return `/dashboard/${appId}`;
  }

  const suffix = normalized.slice(prefix.length);
  return `/dashboard/${appId}${prefix === "/dashboard" ? suffix : `${prefix}${suffix}`}`;
};

const currentHost = () => (typeof window !== "undefined" ? window.location.hostname : "");

// A link to this site (either domain) or to a retired product domain, rebuilt
// on the visitor's own origin. Anything else is a third-party URL: null.
// On a local host (dev, tests) a link already on this site keeps its host.
const toSiteUrl = (link, currentHostname = currentHost()) => {
  const parsedUrl = new URL(link);
  const hostname = parsedUrl.hostname.toLowerCase();
  const isLegacy = Object.prototype.hasOwnProperty.call(LEGACY_APP_HOSTS, hostname);
  if (!SITE_HOSTS.has(hostname) && !isLegacy) return null;
  const local = !currentHostname || isLocalHost(currentHostname);
  if (!(local && SITE_HOSTS.has(hostname))) {
    const site = new URL(siteOriginForHost(currentHostname));
    parsedUrl.protocol = site.protocol;
    parsedUrl.host = site.host;
  }
  // The load balancer serves juristai.org only on the apex (www redirects).
  if (parsedUrl.hostname === "www.juristai.org") parsedUrl.hostname = "juristai.org";
  parsedUrl.searchParams.delete("token");
  return { parsedUrl, isLegacy, legacyAppId: isLegacy ? LEGACY_APP_HOSTS[hostname] : null };
};

// Where a link to a retired product domain lands on the site.
const legacyLandingPath = (pathname, legacyAppId) =>
  legacyAppId ? mapLegacyAppPath(pathname, legacyAppId) : "/dashboard";

const isPracticeAreaPath = (pathname) => /^\/dashboard\/(1|2|3|4|5)(?:\/|$)/.test(pathname);

// `token` is accepted from older callers and ignored (see the note above).
// eslint-disable-next-line no-unused-vars
export const buildPostLoginRedirectUrl = (redirectUrl, token) => {
  if (!redirectUrl) {
    return null;
  }

  if (redirectUrl.startsWith("/") && !redirectUrl.startsWith("//")) {
    return redirectUrl;
  }

  try {
    const site = toSiteUrl(redirectUrl);
    // Never hand a freshly signed-in session to a third-party address.
    if (!site) return null;
    const { parsedUrl, isLegacy, legacyAppId } = site;

    const hubCaseflowSource = parsedUrl.searchParams.get("source");
    const isHubCaseflowReturn = parsedUrl.pathname === "/caseflow" &&
      ["signup", "new_matter"].includes(hubCaseflowSource);
    if (isHubCaseflowReturn) {
      // These routes belong to the home site and stay there so the
      // account-level entry runs before a practice area's CaseFlow.
      return parsedUrl.toString();
    }

    const dashboardAppMatch = parsedUrl.pathname.match(/^\/dashboard\/(\d+)(?:\/|$)/);
    if (!isLegacy && dashboardAppMatch && !["1", "2", "3", "4", "5"].includes(dashboardAppMatch[1])) {
      return null;
    }
    if (isLegacy && !isPracticeAreaPath(parsedUrl.pathname)) {
      parsedUrl.pathname = legacyLandingPath(parsedUrl.pathname, legacyAppId);
    } else if (!isLegacy && parsedUrl.pathname === "/") {
      // A link on this site keeps its own path. Only a practice-area URL or a
      // retired product domain picks a workspace; never default to civil (2).
      parsedUrl.pathname = "/dashboard";
    }
    return parsedUrl.toString();
  } catch (error) {
    console.error("Failed to build post-login redirect url:", error);
    return null;
  }
};

// `token` is accepted from older callers and ignored: practice areas share the
// signed-in session because they are the same origin.
// eslint-disable-next-line no-unused-vars
export const buildAppSsoLaunchUrl = (link, token, currentHostname = currentHost()) => {
  if (!link || link === "#") {
    return null;
  }

  try {
    const site = toSiteUrl(link, currentHostname);
    if (!site) return link;
    const { parsedUrl, isLegacy, legacyAppId } = site;
    if (isLegacy && !isPracticeAreaPath(parsedUrl.pathname)) {
      parsedUrl.pathname = legacyLandingPath(parsedUrl.pathname, legacyAppId);
    }
    return parsedUrl.toString();
  } catch (error) {
    console.error("Failed to build app SSO launch url:", error);
    return null;
  }
};

export const getOrganizationInviteRedirectUrl = (appId, origin = siteOrigin()) => {
  const normalizedAppId =
    appId === undefined || appId === null ? "" : String(appId).trim();
  if (!ORGANIZATION_INVITE_APP_IDS.has(normalizedAppId)) {
    return null;
  }
  try {
    return new URL(`/dashboard/${normalizedAppId}`, origin).toString();
  } catch {
    return `${siteOrigin()}/dashboard/${normalizedAppId}`;
  }
};

export const buildOrganizationInviteLoginUrl = ({
  token,
  email,
  appId,
  redirectUrl,
  baseUrl = `${siteOrigin()}/login`,
} = {}) => {
  if (!token) {
    return baseUrl;
  }

  try {
    const parsedUrl = new URL(baseUrl);
    parsedUrl.searchParams.set("inviteToken", token);

    if (typeof email === "string" && email.trim()) {
      parsedUrl.searchParams.set("inviteEmail", email.trim().toLowerCase());
      parsedUrl.searchParams.set("email", email.trim().toLowerCase());
    }

    const resolvedRedirectUrl =
      (typeof redirectUrl === "string" && redirectUrl.trim()) ||
      getOrganizationInviteRedirectUrl(appId, parsedUrl.origin);
    if (resolvedRedirectUrl) {
      parsedUrl.searchParams.set("redirect_url", resolvedRedirectUrl);
    }

    return parsedUrl.toString();
  } catch (error) {
    console.error("Failed to build organization invite login url:", error);
    return baseUrl;
  }
};
